import { Box, Skeleton, Tab, Tabs, Typography, useTheme, Divider } from '@mui/material';
import React, { FC, useMemo, useState } from 'react';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

export interface RevenueRankItem {
  label: string;
  amount: number;
}

/** Full currency with Indian digit grouping, never abbreviated as L / Cr. */
const formatINR = (value: number) =>
  formatCurrencyWithSymbol(Number(value) || 0);

interface TopPerformersCardProps {
  isLoading: boolean;
  topVenuesMtd?: RevenueRankItem[];
  topEventTypesMtd?: RevenueRankItem[];
}

const TopPerformersCard: FC<TopPerformersCardProps> = ({ isLoading, topVenuesMtd = [], topEventTypesMtd = [] }) => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);

  const items = useMemo(() => {
    const list = tab === 0 ? topVenuesMtd : topEventTypesMtd;
    return (Array.isArray(list) ? list : []).slice(0, 5).map(r => ({
      label: String(r?.label ?? ''),
      amount: typeof (r as any)?.amount === 'number' ? (r as any).amount : Number((r as any)?.amount) || 0,
    }));
  }, [tab, topEventTypesMtd, topVenuesMtd]);

  return (
    <Box
      sx={{
        p: 3,
        pt: 3,
        borderRadius: '14px',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 320,
        boxSizing: 'border-box',
      }}>
      <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.5 }}>Top Performers</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600, mb: 1 }}>
        Month-to-date revenue (confirmed bookings)
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          mb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 36,
            py: 0.5,
            fontSize: 12,
            fontWeight: 800,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&.Mui-selected': {
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              marginBottom: '-2px',
            },
          },
        }}>
        <Tab label="Venues" />
        <Tab label="Event Types" />
      </Tabs>

      {isLoading ? (
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
      ) : items.length > 0 ? (
        <Box component="ol" sx={{ m: 0, pl: 0, pr: 0, flex: 1 }}>
          {items.map((row, idx) => {
            return (
              <Box key={`${row.label}-${idx}`}>
                <Box
                  component="li"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 0.75,
                  }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: idx === 0 ? 'primary.main' : 'grey.400',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#fff',
                      flexShrink: 0,
                    }}>
                    {idx + 1}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'text.primary',
                          lineHeight: 1.3,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                        {row.label}
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: idx === 0 ? 'primary.main' : 'transparent',
                          ml: 1,
                          flexShrink: 0,
                        }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: idx === 0 ? 'primary.contrastText' : 'text.secondary',
                            lineHeight: 1.3,
                          }}>
                          {formatINR(Number(row.amount) || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {idx < items.length - 1 && <Divider sx={{ my: 0.5, borderColor: 'divider' }} />}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            borderRadius: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 700, textAlign: 'center' }}>
            No top performers to show for this month yet.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TopPerformersCard;
